import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { apiUrl } from "../../config/Config";

const Refunds = () => {
  const navigate = useNavigate();
  const userdata = useSelector((store) => store.userReducer.userdata);
  const [refunds, setRefunds] = useState([]);
  const [selectedRefundId, setSelectedRefundId] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [status, setStatus] = useState("requested");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selectedRefund =
    refunds.find((refund) => String(refund.id) === String(selectedRefundId)) ||
    refunds[0] ||
    null;

  const headers = {
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + userdata?.token,
    },
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "NA";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  const formatCurrency = (amount) => `₹ ${Number(amount || 0).toFixed(2)}`;

  const fetchRefunds = async () => {
    setLoading(true);
    await axios
      .post(apiUrl + "getadmincustomerrefunds", {}, headers)
      .then((res) => {
        if (res.data.status === "success") {
          const nextRefunds = Array.isArray(res.data.data) ? res.data.data : [];
          setRefunds(nextRefunds);
          setSelectedRefundId(nextRefunds[0]?.id || null);
          setResponseText(nextRefunds[0]?.admin_response || "");
          setStatus(nextRefunds[0]?.status || "requested");
        } else {
          Swal.fire({
            text: res.data.message,
            icon: "error",
            confirmButtonColor: "rgb(59, 151, 196)",
          });
        }
      })
      .catch((err) => {
        Swal.fire({
          text: err.message,
          icon: "error",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
      });
    setLoading(false);
  };

  const selectRefund = (refund) => {
    setSelectedRefundId(refund.id);
    setResponseText(refund.admin_response || "");
    setStatus(refund.status || "requested");
  };

  const saveResponse = async () => {
    if (!selectedRefund || !responseText.trim()) {
      Swal.fire({
        text: "Please write a response before submitting",
        icon: "error",
        confirmButtonColor: "rgb(59, 151, 196)",
      });
      return;
    }

    setSaving(true);
    await axios
      .post(
        apiUrl + "replycustomerrefund",
        {
          id: selectedRefund.id,
          status,
          admin_response: responseText,
        },
        headers
      )
      .then((res) => {
        if (res.data.status === "success") {
          Swal.fire({
            text: "Refund response saved successfully",
            icon: "success",
            confirmButtonColor: "rgb(59, 151, 196)",
          });
          fetchRefunds();
        } else {
          Swal.fire({
            text: res.data.message,
            icon: "error",
            confirmButtonColor: "rgb(59, 151, 196)",
          });
        }
      })
      .catch((err) => {
        Swal.fire({
          text: err.message,
          icon: "error",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
      });
    setSaving(false);
  };

  useEffect(() => {
    if (userdata && Object.keys(userdata).length > 0) {
      fetchRefunds();
    } else {
      navigate("/admin/login");
    }
  }, []);

  return (
    <div className="admin-question-page">
      <div className="dashboardbox admin-question-layout">
        <div className="admin-question-list">
          <div className="admin-question-header">
            <div>
              <p className="dashboardboxtitle">Refund Requests</p>
              <span>{refunds.length} total</span>
            </div>
          </div>

          {loading ? (
            <p className="admin-question-empty">Loading refunds...</p>
          ) : refunds.length ? (
            refunds.map((refund) => (
              <button
                key={refund.id}
                className={`admin-question-card ${
                  String(selectedRefund?.id) === String(refund.id) ? "active" : ""
                }`}
                onClick={() => selectRefund(refund)}
              >
                <div>
                  <strong>{refund.request_type} - {refund.order_number || `Order #${refund.order_id}`}</strong>
                  <span
                    className="admin-inline-link"
                    onClick={(event) => {
                      event.stopPropagation();
                      refund.customer_id && navigate(`/admin/customer-details/${refund.customer_id}`);
                    }}
                  >
                    {refund.customer_name || "Customer"}
                  </span>
                </div>
                <p>{formatDate(refund.created_at)}</p>
                <small className={`admin-question-status ${refund.status}`}>
                  {refund.status || "requested"}
                </small>
              </button>
            ))
          ) : (
            <p className="admin-question-empty">No refund requests yet.</p>
          )}
        </div>

        <div className="admin-question-detail">
          {selectedRefund ? (
            <>
              <div className="admin-question-detail-head">
                <div>
                  <span>{selectedRefund.customer_email}</span>
                  <h3>{selectedRefund.request_type} Request</h3>
                </div>
                <strong className={`admin-question-status ${selectedRefund.status}`}>
                  {selectedRefund.status || "requested"}
                </strong>
              </div>

              <div className="admin-question-message">
                <label>Refund Details</label>
                <p>
                  <strong>Order:</strong> {selectedRefund.order_number || `Order #${selectedRefund.order_id}`}<br />
                  <strong>Amount:</strong> {formatCurrency(selectedRefund.refund_amount)}<br />
                  <strong>Reason:</strong> {selectedRefund.reason}<br />
                  {selectedRefund.message}
                </p>
              </div>

              <div className="admin-question-reply-box">
                <label>Status</label>
                <select
                  value={status}
                  onChange={(event) => setStatus(event.target.value)}
                  className="admin-refund-status-select"
                >
                  <option value="requested">Requested</option>
                  <option value="under_review">Under Review</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="admin-question-reply-box">
                <label>Admin Response</label>
                <textarea
                  value={responseText}
                  onChange={(event) => setResponseText(event.target.value)}
                  placeholder="Write your response to the customer"
                />
              </div>

              <button className="webcustom-btn admin-question-save" onClick={saveResponse}>
                {saving ? "Saving..." : "Save Response"}
              </button>
            </>
          ) : (
            <p className="admin-question-empty">Select a refund request to respond.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Refunds;
